import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import ReviewForm from '../components/ReviewForm';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

function StatusBadge({ status }) {
  const map = {
    pending:   { bg:'#FFF9E6', color:'#C9930A', label:'Pending' },
    confirmed: { bg:'#EAF5EC', color:'#2D7A3A', label:'Confirmed' },
    active:    { bg:'#EAF5EC', color:'#2D7A3A', label:'Active' },
    shipped:   { bg:'#EBF0FF', color:'#1A5276', label:'Shipped' },
    delivered: { bg:'#EBF0FF', color:'#1A5276', label:'Delivered' },
    completed: { bg:'#EAF5EC', color:'#2D7A3A', label:'Completed' },
    cancelled: { bg:'#F7F7F7', color:'#888',    label:'Cancelled' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ fontSize:13, fontWeight:700, padding:'4px 12px', borderRadius:100, background:s.bg, color:s.color }}>
      {s.label}
    </span>
  );
}

export default function OrderDetailPage() {
  const { id }         = useParams();
  const { search }     = useLocation();
  const navigate       = useNavigate();
  const { user }       = useAuth();
  const { addToCart }  = useCart();
  const recordType     = new URLSearchParams(search).get('type') || 'order';
  const isRental       = recordType === 'booking';

  const [record,    setRecord]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [cancelling,setCancelling]= useState(false);
  const [confirming,setConfirming]= useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const endpoint = isRental ? `/bookings/${id}` : `/orders/${id}`;
    API.get(endpoint)
      .then(r => setRecord(isRental ? r.data.booking : r.data.order))
      .catch(() => navigate('/dashboard/orders'))
      .finally(() => setLoading(false));
  }, [id, recordType]);

  const handleConfirmReceipt = async () => {
    setConfirming(true);
    try {
      await API.patch(`/orders/${id}/confirm-receipt`);
      setRecord(p => ({ ...p, status: 'completed', receipt_confirmed_at: new Date().toISOString() }));
      toast.success('Receipt confirmed — escrow will be released to the seller.');
    } catch {
      toast.error('Failed to confirm receipt');
    } finally { setConfirming(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? This cannot be undone.')) return;
    setCancelling(true);
    try {
      await API.patch(`/orders/${id}/cancel`, { reason: 'Cancelled by buyer' });
      setRecord(p => ({ ...p, status: 'cancelled' }));
      toast.success('Order cancelled. The listing has been restored.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not cancel order');
    } finally { setCancelling(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <div style={{ fontSize:48 }} className="float-anim">🍒</div>
      <div style={{ fontSize:14, color:'var(--text-soft)' }}>Loading...</div>
    </div>
  );

  if (!record) return null;

  const isBuyer        = isRental ? record.renter_id === user.id : record.buyer_id === user.id;
  const hoursSince     = (Date.now() - new Date(record.created_at).getTime()) / 3600000;
  const canCancel      = isBuyer && record.status === 'pending' && hoursSince <= 24 && !isRental;
  const hoursRemaining = Math.max(0, Math.ceil(24 - hoursSince));

  const itemPrice   = parseFloat(record.item_price || record.subtotal || 0);
  const shippingCost= parseFloat(record.shipping_cost || 0);
  const platformFee = parseFloat(record.platform_fee || 0);
  const deposit     = parseFloat(record.deposit_amount || 0);
  const total       = parseFloat(record.total_charged || 0);

  return (
    <div style={{ maxWidth:860, margin:'0 auto', padding:'40px 48px 80px' }}>
      {/* Breadcrumb */}
      <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:24, display:'flex', gap:6, alignItems:'center' }}>
        <span style={{ cursor:'pointer', color:'var(--cherry)' }} onClick={() => navigate('/dashboard/orders')}>
          ← Back to Orders
        </span>
      </div>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28, flexWrap:'wrap', gap:16 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700 }}>
              {isRental ? '📅 Rental Booking' : '📦 Order'} #{id.slice(0,8).toUpperCase()}
            </h1>
            <StatusBadge status={record.status} />
          </div>
          <div style={{ fontSize:13, color:'var(--text-soft)' }}>
            Placed {record.created_at ? format(new Date(record.created_at), 'MMMM d, yyyy \'at\' h:mm a') : ''}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {record.listing_type === 'auction' && record.status === 'pending' && isBuyer && (
            <button className="btn btn-cherry" onClick={() => {
              addToCart(
                { id: record.listing_id, title: record.title, listing_type: 'auction', price: record.item_price, seller_id: record.seller_id, primary_image: record.primary_image },
                { existingOrderId: record.id, existingOrderNumber: record.order_number }
              );
              navigate('/checkout');
            }}>
              💳 Complete Purchase
            </button>
          )}
          {record.status === 'delivered' && isBuyer && !isRental && (
            <button className="btn btn-green" onClick={handleConfirmReceipt} disabled={confirming}>
              {confirming ? 'Confirming...' : '✅ Confirm Receipt'}
            </button>
          )}
          {canCancel && (
            <button className="btn" style={{ background:'#FDECEA', color:'var(--cherry)', border:'1px solid rgba(192,57,43,.2)', borderRadius:10, padding:'10px 20px', fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:700, cursor:'pointer' }}
              onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : '✕ Cancel Order'}
            </button>
          )}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:24, alignItems:'start' }}>
        {/* LEFT col */}
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Item card */}
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:16 }}>Item</div>
            <div style={{ display:'flex', gap:16, alignItems:'center' }}>
              <div style={{ width:80, height:80, borderRadius:12, background:'var(--cream)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:32 }}>
                {record.primary_image
                  ? <img src={`${API_BASE}${record.primary_image}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : (isRental ? '📅' : '📦')
                }
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>{record.title}</div>
                {isRental && record.start_date && (
                  <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom:4 }}>
                    📅 {format(new Date(record.start_date), 'MMM d')} – {format(new Date(record.end_date), 'MMM d, yyyy')}
                    {' · '}{record.days} day{record.days !== 1 ? 's' : ''}
                  </div>
                )}
                <div style={{ fontSize:13, color:'var(--text-soft)' }}>
                  {isRental ? 'Rental listing' : record.listing_type === 'auction' ? 'Auction win' : 'Fixed-price sale'}
                </div>
                <button className="btn btn-ghost btn-sm" style={{ marginTop:10 }}
                  onClick={() => navigate(`/listing/${record.listing_id}`)}>
                  View Listing →
                </button>
              </div>
            </div>
          </div>

          {/* Shipping / pickup info */}
          {!isRental && (
            <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:16 }}>
                📦 Shipping Details
              </div>
              {record.ship_to_name && (
                <div style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.8 }}>
                  <div style={{ fontWeight:600 }}>{record.ship_to_name}</div>
                  {record.ship_to_address && <div>{record.ship_to_address}</div>}
                  <div>{[record.ship_to_city, record.ship_to_state, record.ship_to_zip].filter(Boolean).join(', ')}</div>
                </div>
              )}
              {record.tracking_number && (
                <div style={{ marginTop:14, padding:'12px 14px', background:'#EBF0FF', borderRadius:10 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#1A5276', marginBottom:4 }}>TRACKING NUMBER</div>
                  <div style={{ fontFamily:'monospace', fontSize:15, fontWeight:700 }}>{record.tracking_number}</div>
                  {record.shipping_carrier && (
                    <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:3 }}>via {record.shipping_carrier}</div>
                  )}
                </div>
              )}
              {!record.tracking_number && record.status === 'pending' && (
                <div style={{ fontSize:13, color:'var(--text-soft)', marginTop:8 }}>
                  Awaiting shipment from seller.
                </div>
              )}
              {record.shipped_at && (
                <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:10 }}>
                  Shipped {format(new Date(record.shipped_at), 'MMM d, yyyy')}
                </div>
              )}
            </div>
          )}

          {/* Seller / renter info */}
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:16 }}>
              {isBuyer ? (isRental ? '🏠 Owner' : '🏪 Seller') : (isRental ? '👤 Renter' : '👤 Buyer')}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0 }}>
                {(isBuyer
                  ? (record.seller_first || record.owner_first_name || record.other_first_name || '?')
                  : (record.buyer_first  || record.renter_first_name || record.other_first_name || '?')
                )[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>
                  {isBuyer
                    ? `${record.seller_first || record.other_first_name || ''} ${record.seller_last || record.other_last_name || ''}`
                    : `${record.buyer_first  || record.other_first_name || ''} ${record.buyer_last  || record.other_last_name || ''}`
                  }
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" style={{ marginLeft:'auto' }}
                onClick={() => navigate(`/messages`)}>
                💬 Message
              </button>
            </div>
          </div>

          {/* Cancellation notice */}
          {canCancel && (
            <div style={{ background:'#FFF9E6', border:'1px solid rgba(201,147,10,.3)', borderRadius:14, padding:'14px 18px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#C9930A', marginBottom:4 }}>
                ⏱ Cancellation window open
              </div>
              <div style={{ fontSize:13, color:'var(--text-mid)' }}>
                You can cancel this order within 24 hours of purchase, as long as it hasn't shipped.
                You have approximately <strong>{hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''}</strong> remaining.
              </div>
            </div>
          )}

          {/* Receipt confirmation note */}
          {record.receipt_confirmed_at && (
            <div style={{ background:'#EAF5EC', border:'1px solid rgba(45,122,58,.2)', borderRadius:14, padding:'14px 18px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#2D7A3A' }}>
                ✅ Receipt confirmed {format(new Date(record.receipt_confirmed_at), 'MMM d, yyyy')}
              </div>
              <div style={{ fontSize:13, color:'var(--text-mid)', marginTop:4 }}>
                Escrow has been released to the seller.
              </div>
            </div>
          )}
        </div>

        {/* RIGHT col — Order summary */}
        <div style={{ position:'sticky', top:88 }}>
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:18 }}>
              Order Summary
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:10, fontSize:14, marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text-mid)' }}>
                  {isRental ? `${record.days} day${record.days !== 1 ? 's' : ''} rental` : 'Item price'}
                </span>
                <span style={{ fontWeight:600 }}>${itemPrice.toFixed(2)}</span>
              </div>
              {shippingCost > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:'var(--text-mid)' }}>Shipping</span>
                  <span style={{ fontWeight:600 }}>${shippingCost.toFixed(2)}</span>
                </div>
              )}
              {deposit > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:'var(--text-mid)' }}>Security deposit (refundable)</span>
                  <span style={{ fontWeight:600 }}>${deposit.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text-mid)' }}>Platform fee (10%)</span>
                <span style={{ fontWeight:600 }}>${platformFee.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:14, borderTop:'1px solid var(--border)' }}>
              <span style={{ fontWeight:700 }}>Total charged</span>
              <span style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700 }}>
                ${total.toFixed(2)}
              </span>
            </div>

            <div style={{ marginTop:18, display:'flex', flexDirection:'column', gap:6 }}>
              {['🔒 Escrow protected', '💳 Payment secured', '🛡️ Buyer guarantee'].map(t => (
                <div key={t} style={{ fontSize:12, color:'var(--text-soft)' }}>{t}</div>
              ))}
            </div>
          </div>

          {/* Order timeline */}
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24, marginTop:16 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontWeight:700, marginBottom:16 }}>
              Timeline
            </div>
            {[
              { icon:'🛒', label:'Order placed',     done: true,                    date: record.created_at },
              { icon:'📦', label:'Shipped',          done: !!record.shipped_at,     date: record.shipped_at },
              { icon:'🏠', label:'Delivered',        done: record.status === 'delivered' || record.status === 'completed', date: null },
              { icon:'✅', label:'Completed',        done: record.status === 'completed', date: record.receipt_confirmed_at },
            ].map((step, i) => !isRental && (
              <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom: i < 3 ? 16 : 0 }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background: step.done ? 'var(--cherry)' : 'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                    {step.done ? '✓' : step.icon}
                  </div>
                  {i < 3 && <div style={{ width:2, height:16, background: step.done ? 'var(--cherry)' : 'var(--border)', marginTop:4 }} />}
                </div>
                <div style={{ paddingTop:4 }}>
                  <div style={{ fontSize:13, fontWeight: step.done ? 700 : 400, color: step.done ? 'var(--text-primary)' : 'var(--text-soft)' }}>
                    {step.label}
                  </div>
                  {step.date && (
                    <div style={{ fontSize:11, color:'var(--text-soft)' }}>
                      {format(new Date(step.date), 'MMM d, h:mm a')}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isRental && (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {[
                  { icon:'📅', label:'Booked',    done:true,                              date: record.created_at },
                  { icon:'✅', label:'Confirmed',  done: ['confirmed','active','completed'].includes(record.status), date:null },
                  { icon:'🏠', label:'Active',     done: ['active','completed'].includes(record.status),            date: record.start_date },
                  { icon:'🎉', label:'Completed',  done: record.status === 'completed',    date: record.end_date },
                ].map((step, i) => (
                  <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background: step.done ? 'var(--cherry)' : 'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                      {step.done ? '✓' : step.icon}
                    </div>
                    <div style={{ paddingTop:4 }}>
                      <div style={{ fontSize:13, fontWeight: step.done ? 700 : 400, color: step.done ? 'var(--text-primary)' : 'var(--text-soft)' }}>
                        {step.label}
                      </div>
                      {step.date && (
                        <div style={{ fontSize:11, color:'var(--text-soft)' }}>
                          {format(new Date(step.date), 'MMM d')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Review Form */}
          {record.status === 'completed' && isBuyer && (
            <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:24 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:700, marginBottom:20 }}>
                📝 Share Your Feedback
              </div>
              <ReviewForm
                referenceId={record.id}
                referenceType={isRental ? 'booking' : 'order'}
                revieweeId={isRental ? record.owner_id : record.seller_id}
                listingId={record.listing_id}
                onSuccess={() => {
                  toast.success('Review submitted! Thank you for your feedback.');
                  setRecord(p => ({ ...p, has_review: true }));
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
